import { Component, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { EBackendFilterTypes, IGenericCrudConfiguration } from 'src/app/libs/components/generic-crud/generic-crud.component';
import {
  Appointment, AppointmentsFilterStates, AppointmentsStates,
  EAppointmentsFilterStates, EAppointmentsStates, IEAppointmentsStates, IIdentifiable, ScheduleAppointmentsStates, User
} from 'src/app/models';
import { MasterApiService } from 'src/app/services/master-api.service';
import { StatesDirectorService } from 'src/app/services/states-director.service';
import { AdminAppointmentsAddComponent } from './admin-appointments-add/admin-appointments-add.component';
import { AdminAppointmentsEditComponent } from './admin-appointments-edit/admin-appointments-edit.component';

@Component({
    selector: 'app-admin-appointments',
    templateUrl: './admin-appointments.component.html',
    styleUrls: ['./admin-appointments.component.sass'],
    standalone: false
})
export class AdminAppointmentsComponent implements OnInit {
  config?: IGenericCrudConfiguration<Appointment>;
  appointmentStates = AppointmentsStates;
  appointmentFilterStates = AppointmentsFilterStates;
  username = localStorage.getItem('user') || '';
  users: User[] = [];

  constructor(private api: MasterApiService, private translateService: TranslateService, private stateDirector: StatesDirectorService) {
    Object.assign(this.appointmentFilterStates, AppointmentsFilterStates);
    this.appointmentFilterStates.forEach(x => x.displayName = this.translateService.instant(`enumeration.AppointmentsFilterStates.${x.name}`));
   }
  

  async ngOnInit(): Promise<void> {
    this.users = await firstValueFrom(this.api.resources.auth.users.getAll());

    const stateTypes = AppointmentsStates;
    stateTypes.forEach(x => x.displayName = this.translateService.instant(`enumeration.AppointmentsStates.${x.name}`));

    this.appointmentFilterStates.forEach(x => x.displayName = this.translateService.instant(`enumeration.AppointmentsFilterStates.${x.name}`));

    const scheduleStatus = ScheduleAppointmentsStates;
    scheduleStatus.forEach(x => x.displayName = this.translateService.instant(`enumeration.ScheduleAppointmentsStates.${x.name}`));

    this.config = {
      name: this.api.resources.configuration.appointments.resourceName,
      resource: this.api.resources.configuration.appointments,
      createComponent: AdminAppointmentsAddComponent,
      editComponent: AdminAppointmentsEditComponent,
      entityFields: {
        originUsername: {
          dataType: 'shortstring',
          lookup: {
            dataSource: this.users,
            displayExpr: 'fullName',
            valueExpr: 'userName'
          },
          isLink: true
        },
        destinationUsername: {
          dataType: 'shortstring',
          lookup: {
            dataSource: this.users,
            displayExpr: 'fullName',
            valueExpr: 'userName'
          }
        },
        startActivationDateTime: { dataType: 'datetime' },
        endActivationDateTime: { dataType: 'datetime' },
        notificationMinutes: { visible: false, dataType: 'number'},
        subject: { isLink: true, dataType: 'string'},
        text: {caption: 'entity.appointment.message', dataType: 'longstring' },
        creationDateTime: { visible: false, dataType: 'datetime' },
        hasIncidents: { visible: false, dataType: 'boolean'},
        stateId: {
          dataType: 'code',
          lookup: {
            dataSource: stateTypes,
            displayExpr: 'displayName',
            valueExpr: 'id',
          },
          cellTemplate: 'stateCellTpl'
        },
      },
      backendFilters: [
      {
        type: EBackendFilterTypes.Selection,
        queryParam: 'filterStateId',
        config: {
          value: EAppointmentsFilterStates.All,
          label: 'entity.appointment.stateId',
          items: this.appointmentFilterStates,
          displayExpr: 'displayName',
          valueExpr: 'id',
        }
      }, {
        type: EBackendFilterTypes.Selection,
        queryParam: 'destinationUserName',
        config: {
          label: 'entity.appointment.destinationUsername',
          items: this.users.map(u => ({
            name: u.fullName,
            userName: u.userName,
          })),
          value: this.users.find(u => u.userName === this.username)?.userName,
          displayExpr: 'name',
          valueExpr: 'userName',
        },
      }],
      changeStateAction: (e) => this.changeState(e),
      archiveAction: e => this.archive(e),
      stateField: 'stateId',
      usesCardVision: false,
      usesKanbanVision: true,
      usesCalendarVision: true
    };
  }

  async changeState(e: {element: Appointment, destinationStateId: number}) : Promise<void> {
    if ('stateId' in e.element){
      //const rs = await firstValueFrom(this.master.resources.appointments.changeState(e.element.id, e.destinationStateId)) as Appointment;
      const rs = await firstValueFrom(this.api.resources.configuration.appointments.changeState(e.element.id, e.destinationStateId)) as Appointment;
      e.element.stateId = rs.stateId;
      e.element.scheduleStateId = rs.scheduleStateId;
      e.element = rs;
    }
  }

  async archive(e: IIdentifiable & IEAppointmentsStates) : Promise<void> {
    if ('stateId' in e && e.stateId !== EAppointmentsStates.Archived){
      const rs = await firstValueFrom(this.api.resources.configuration.appointments.changeState(e.id, EAppointmentsStates.Archived)) as Appointment;
      e.stateId = rs.stateId;
      e = rs;
    }
  }
}
